import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { ChargepointType } from './chargepoints-api.service';

const HOST = environment.serverUrl;
const TRANSACTIONS_API_URL = HOST + '/transactions';

export enum TransactionStatus {
  Accepted = 'Accepted',
  Rejected = 'Rejected',
  Started = 'Started',
  Stopping = 'Stopping',
  Stopped = 'Stopped'
}

export interface AbstractTransaction {
  id: string;
  type: ChargepointType;
  status: TransactionStatus;
  chargepointId: string;
  reservationId?: string;
  creationTime: Date;
  startTime?: Date;
  endTime?: Date;
}

export interface OCPPTransaction extends AbstractTransaction {
  connectorId: number;
  ocppTransactionId: number;
  meterStart: number;
  meterCurrent?: number;
  meterStop?: number;
}

export interface OkeChargeTransaction extends AbstractTransaction {
  chipId: string;
  energy: number;
}

export interface IvyTransaction extends AbstractTransaction {
  plugId: string;
  energy: number;
  initialEnergy: number;
  startTimeAcknowledge?: Date;
  endTimeAcknowledge?: Date;
}

export type Transaction = OCPPTransaction | OkeChargeTransaction | IvyTransaction;

export interface StartTransactionRequest {
  chargepointId: string;
  connectorId?: number;
  type: ChargepointType;
  chipId?: string;
  plugId?: string;
}

function reviveTransaction(transaction: Transaction): Transaction {
  transaction.creationTime = transaction.creationTime ? new Date(transaction.creationTime) : new Date(0);
  transaction.startTime = transaction.startTime ? new Date(transaction.startTime) : undefined;
  transaction.endTime = transaction.endTime ? new Date(transaction.endTime) : undefined;

  if (transaction.type === ChargepointType.IVY) {
    const ivyTransaction = transaction as IvyTransaction;
    ivyTransaction.startTimeAcknowledge = ivyTransaction.startTimeAcknowledge ? new Date(ivyTransaction.startTimeAcknowledge) : undefined;
    ivyTransaction.endTimeAcknowledge = ivyTransaction.endTimeAcknowledge ? new Date(ivyTransaction.endTimeAcknowledge) : undefined;
  }

  return transaction;
}

export interface TransactionSearchCriteria {
  type?: ChargepointType;
  chargepointId?: string;
}

@Injectable({ providedIn: 'root' })
export class TransactionApiService {

  constructor(private http: HttpClient) { }

  public searchTransactions(searchCriteria: TransactionSearchCriteria): Observable<Transaction[]> {
    return this.http.post<Transaction[]>(`${TRANSACTIONS_API_URL}/search`, {...searchCriteria}, { responseType: 'json' })
      .pipe(
        map(transactions => transactions.map(reviveTransaction))
      );
  }

  public getTransactionById(id: string): Observable<Transaction> {
    return this.http.get<Transaction>(`${TRANSACTIONS_API_URL}/${id}`).pipe(
      map(transaction => reviveTransaction(transaction))
    );
  }

  public startTransaction(startTransactionRequest: StartTransactionRequest): Observable<Transaction> {
    return this.http.post<Transaction>(`${TRANSACTIONS_API_URL}`, startTransactionRequest).pipe(
      map(transaction => reviveTransaction(transaction))
    );
  }

  public stopTransaction(transactionId: string): Observable<void> {
    return this.http.post<void>(`${TRANSACTIONS_API_URL}/${transactionId}/stop`, {});
  }

}
